/**
 * The app's one source of device positions: `navigator.geolocation`, behind a
 * seam the geolocation middleware watches through.
 *
 * It exists so staff can pretend to stand somewhere (Home location debug). A
 * pretend fix is reported to every watcher exactly as the browser would report
 * a real one, so everything downstream (the geolocation slice, the map's
 * current-position marker, the User-Coordinates header, "Use current
 * location", near me) follows it without knowing. With no pretend fix set,
 * which is always the case outside debug mode, this is pure delegation.
 */

import { nowMillis } from "@/lib/date-utils"

export type PretendFix = {
  latitude: number
  longitude: number
  accuracy: number | null
}

type Watcher = {
  success: PositionCallback
  error: PositionErrorCallback
  options: PositionOptions | undefined
  /** the browser's own watch id, while this watcher is on the real device */
  realId: number | null
}

const watchers = new Map<number, Watcher>()
let nextWatchId = 1
let pretendFix: PretendFix | undefined

function report(watcher: Watcher, fix: PretendFix) {
  const coords = {
    latitude: fix.latitude,
    longitude: fix.longitude,
    accuracy: fix.accuracy ?? 0,
    altitude: null,
    altitudeAccuracy: null,
    heading: null,
    speed: null,
    toJSON() {
      return this
    },
  } satisfies GeolocationCoordinates
  watcher.success({
    coords,
    timestamp: nowMillis(),
    toJSON() {
      return this
    },
  } satisfies GeolocationPosition)
}

/** Whether positions are being made up right now (never persist those). */
export function isPretending() {
  return pretendFix !== undefined
}

export function watchPosition(
  success: PositionCallback,
  error: PositionErrorCallback,
  options?: PositionOptions
): number {
  const id = nextWatchId++
  const watcher: Watcher = { success, error, options, realId: null }
  watchers.set(id, watcher)
  if (pretendFix) {
    // After this call returns, as the browser does: the caller registers the
    // watch with the id it gets back, and a fix that lands before that is
    // dropped by the state machine, which then waits forever for another.
    setTimeout(() => {
      if (pretendFix && watchers.get(id) === watcher) {
        report(watcher, pretendFix)
      }
    }, 0)
  } else {
    watcher.realId = navigator.geolocation.watchPosition(
      success,
      error,
      options
    )
  }
  return id
}

export function clearWatch(id: number) {
  const watcher = watchers.get(id)
  if (watcher?.realId != null) navigator.geolocation.clearWatch(watcher.realId)
  watchers.delete(id)
}

/**
 * Staff debug only (see applyDebugPosition, which refuses outside debug mode).
 * A fix: every watcher leaves the real device and hears this position instead.
 * `undefined`: every watcher goes back to the real device.
 */
export function setPretendFix(fix: PretendFix | undefined) {
  pretendFix = fix
  watchers.forEach((watcher) => {
    if (fix) {
      if (watcher.realId != null) {
        navigator.geolocation.clearWatch(watcher.realId)
        watcher.realId = null
      }
      report(watcher, fix)
    } else if (watcher.realId == null) {
      watcher.realId = navigator.geolocation.watchPosition(
        watcher.success,
        watcher.error,
        watcher.options
      )
    }
  })
}
